import React from 'react';
import _ from 'lodash';
import { Box, Grid, Stack } from '@mui/material';
import { ConferencePageHeader } from './PageHeader';
import { Confirmation } from '../components/modals/common';
import { KY_STATUS_CONFIG, WORKER_TYPES } from '../utilities/constants';
import EqualIcon from '../images/icon/equal.svg';
import NextKYIcon from '../images/icon/icon--arrow-right-5.svg';
import MultiplicationIcon from '../images/icon/multiplication.svg';
import PreviousKYIcon from '../images/icon/icon--arrow-left-5.svg';
import RightArrow from '../images/icon/icon--arrow-ky-right-1.svg';
import { messageUtility } from '../utilities/messageUtility';
import dialogLoader from '../utilities/dialogLoader';
import EventMg, { EventType } from '../utilities/EventManager';
import { CloseEditView } from './CloseEditView';

const CIRCLE_NUMBER = ['①', '②', '③', '④'];
const CONFIRM_TYPE = {
  REJECT: 2,
  APPROVE: 3,
};
const dialogInfo = {
  reject: {
    title: '現地KYの差戻し',
    message: 'こちらの内容で現地KYを差戻しします。よろしいでしょうか。',
    cancelButtonText: messageUtility.getMessage('I-000-017'),
    confirmButtonText: '差戻し',
    effectiveButtonClassName: 'btn--negative--filled',
  },
  confirm: {
    title: '現地KYの確認',
    message: 'こちらの内容で現地KYを受領します。よろしいでしょうか。',
    cancelButtonText: messageUtility.getMessage('I-000-017'),
    confirmButtonText: messageUtility.getMessage('E-000-005-01'),
    effectiveButtonClassName: 'btn--effective--filled',
  },
};

export class DetailKYReport extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      comment: '',
      confirmationDialog: null,
    };
    this.isNavigatingFlag = false;
    this.isSubmittingFlag = false;
  }

  updateStatus = async status => {
    if (this.isSubmittingFlag) return;
    this.isSubmittingFlag = true;
    const { comment } = this.state;
    await this.props.onUpdateStatus({ status, comment });
    this.setState({ confirmationDialog: null });
    this.isSubmittingFlag = false;
  };

  componentDidUpdate(prevProps) {
    if (prevProps.kyReportData !== this.props.kyReportData) {
      this.setState({ comment: this.props.kyReportData.comment || '' });
      EventMg.trigger(EventType.LAYOUT_UPDATE, {});
    }
  }

  handleOpenConfirmationDialog = isConfirm => {
    this.setState({
      confirmationDialog: isConfirm
        ? {
            ...dialogInfo.confirm,
            onConfirm: () => this.updateStatus(CONFIRM_TYPE.APPROVE),
          }
        : {
            ...dialogInfo.reject,
            onConfirm: () => this.updateStatus(CONFIRM_TYPE.REJECT),
          },
    });
  };

  handleCommentChange = event => this.setState({ comment: event.target.value });

  handleNavigate = isNext => {
    const isDirty =
      this.state.comment !== (this.props.kyReportData.comment || '');
    if (isDirty) {
      dialogLoader.show(CloseEditView, {
        callback: {
          clear: () => {
            this.props.performNavigate(isNext);
            dialogLoader.close();
          },
          close: () => {
            dialogLoader.close();
          },
        },
      });
    } else {
      this.props.performNavigate(isNext);
    }
  };

  render() {
    const { comment } = this.state;
    const { kyReportData, warning_threshold } = this.props;
    const isLoading = _.isEmpty(kyReportData);
    const {
      ky_report_works: kyReportWorks,
      focus_action: focusAction,
      ky_report_confirm_items: kyReportConfirmItems,
      ky_report_images: kyReportImages,
      ky_report_participants: kyReportParticipants,
      status: kyReportStatus,
    } = kyReportData;

    return (
      <Stack className='main'>
        <React.Fragment>
          <DetailKyHeader
            close={this.props.onClose}
            onClickExportReport={this.props.onClickExportReport}
            data={kyReportData}
          />
          {isLoading ? (
            <div className='indicator' />
          ) : (
            <div className='main__body'>
              <div className='detail-ky-report'>
                {_.map(kyReportWorks, (kyReportWork, index) => {
                  const notShowIndex = _.size(kyReportWorks) === 1;
                  return (
                    <KYReportWork
                      key={index}
                      data={kyReportWork}
                      index={index}
                      notShowIndex={notShowIndex}
                      warning_threshold={warning_threshold}
                    />
                  );
                })}
                <Box
                  className='detail-ky-report__box-wrapper lighter-gray-box'
                  sx={{ marginBottom: '16px' }}
                >
                  {isShowConfigItem(
                    _.omit(kyReportConfirmItems, 'contact_note')
                  ) && <ReportConfirm data={kyReportConfirmItems} />}
                  <ContactNote data={kyReportConfirmItems} />
                  <FocusAction focusAction={focusAction} />
                  <ReportParticipants
                    data={kyReportParticipants}
                    images={kyReportImages}
                  />
                  <CommentArea
                    comment={comment}
                    onCommentChange={this.handleCommentChange}
                  />
                </Box>
                <RejectConfirm
                  openConfirmDialog={() =>
                    this.handleOpenConfirmationDialog(true)
                  }
                  openRejectDialog={() =>
                    this.handleOpenConfirmationDialog(false)
                  }
                  kyReportStatus={kyReportStatus}
                />
              </div>
              <div className='detail-ky-report__footer'>
                <DetailKYFooter
                  navigate={this.handleNavigate}
                  hasNavigate={this.props.hasNavigate}
                />
              </div>
            </div>
          )}
        </React.Fragment>
        <Confirmation
          {...this.state.confirmationDialog}
          visible={!!this.state.confirmationDialog}
          onCancel={() => this.setState({ confirmationDialog: null })}
        />
      </Stack>
    );
  }
}

const DetailKyHeader = ({ close, onClickExportReport, data }) => {
  return (
    <ConferencePageHeader
      title={
        <Stack direction='row' spacing='10px' alignItems='center'>
          <i
            className='detail-ky-report__btn-close i-close__header i-2_4x'
            onClick={close}
          />
          <Box className='detail-ky-report__description--title' flexShrink={0}>
            現地KY実施者
          </Box>
          <div className='detail-ky-report__highlight'>
            {`${data.user_name_sei || ''} ${data.user_name_mei || ''}${
              data.company_name ? `（${data.company_name}）` : ''
            }`}
          </div>
        </Stack>
      }
      rightComponent={
        <button
          className='btn-icon--sm'
          onClick={onClickExportReport}
          disabled={_.isEmpty(data)}
        >
          <i
            className={`i-print-report${
              _.isEmpty(data) ? '-inactive' : ''
            } i-2x`}
            aria-hidden='true'
          />
        </button>
      }
    />
  );
};

const KYReportWork = ({ data, index, notShowIndex, warning_threshold }) => {
  const workInfo = [
    { title: '会社名', description: _.get(data, 'company_name') },
    { title: '作業場所', description: _.get(data, 'work_place') },
    ...(data.safety_instruction !== null
      ? [{ title: '安全指示事項', description: data.safety_instruction }]
      : []),
  ];
  const { ky_report_work_risk_assessments: kyReportWorkRiskAssessments } = data;

  return (
    <React.Fragment>
      <div className='detail-ky-report__title'>
        作業内容{notShowIndex ? '' : ` ${index + 1}`}：
        {_.get(data, 'work_content') || ''}
      </div>
      <Box
        className='detail-ky-report__box-wrapper dark-gray-box'
        sx={{ marginBottom: '16px' }}
      >
        {data.work_schedule_id && (
          <Stack
            direction='row'
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '8px',
            }}
            spacing={1}
          >
            <Box
              className='detail-ky-report__text-wrapper'
              sx={{ width: '40%' }}
            >
              <Stack spacing={1}>
                {_.map(workInfo, (info, index) => (
                  <Stack
                    direction='row'
                    alignItems='start'
                    spacing={1}
                    key={`work-info-${index}`}
                  >
                    <Box
                      sx={{ minWidth: '84px' }}
                      className='detail-ky-report__description--title'
                    >
                      {info.title}
                    </Box>
                    <Box
                      sx={{ flex: 1 }}
                      className='detail-ky-report__highlight'
                    >
                      {info.description}
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </Box>
            <Box
              className='detail-ky-report__text-wrapper'
              sx={{ width: '40%' }}
            >
              <Stack direction='row' alignItems='start' spacing={1}>
                <Box
                  sx={{ minWidth: '84px' }}
                  className='detail-ky-report__description--title'
                >
                  職種/職長
                </Box>
                <Box
                  sx={{ flex: 1 }}
                  className='detail-ky-report__highlight'
                >
                  {`${_.get(data, 'work_job') || ''}/${_.get(
                    data,
                    'user_name_sei'
                  ) || ''} ${_.get(data, 'user_name_mei') || ''}`}
                </Box>
              </Stack>
            </Box>
            <Box
              className='detail-ky-report__text-wrapper'
              sx={{
                width: '20%',
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-start',
              }}
            >
              <Stack direction='row' alignItems='center' spacing={1}>
                <Box
                  sx={{ width: '24%', minWidth: 'fit-content' }}
                  className='detail-ky-report__description--title'
                >
                  予定人工
                </Box>
                <Box
                  sx={{ width: '76%' }}
                  className='detail-ky-report__highlight'
                >
                  {`${
                    _.toNumber(_.get(data, 'dayworker_type')) ===
                    WORKER_TYPES.contractor.id
                      ? '請負'
                      : '常傭'
                  } ${_.get(data, 'calcd_sum_man_hours') || ''}`}
                </Box>
              </Stack>
            </Box>
          </Stack>
        )}
        {_.map(
          kyReportWorkRiskAssessments,
          (kyReportWorkRiskAssessment, index) => (
            <RiskAssessments
              data={kyReportWorkRiskAssessment}
              index={CIRCLE_NUMBER[index]}
              warning_threshold={warning_threshold}
            />
          )
        )}
      </Box>
    </React.Fragment>
  );
};

const RiskAssessments = ({ data, index, warning_threshold }) => {
  return (
    <Box
      className='detail-ky-report__box-wrapper'
      key={`riskAssessment-${index}`}
    >
      <Stack direction='row' alignItems='center'>
        <Risk
          riskPoint={_.get(data, 'risk_point') || ''}
          possibility={_.get(data, 'risk_possibility_level') || 0}
          severity={_.get(data, 'risk_severity_level') || 0}
          index={index}
        />
        <Box sx={{ width: '20%', textAlign: 'center' }}>
          <img src={RightArrow} alt='' />
        </Box>
        <ReassessedRisk
          riskReduction={_.get(data, 'risk_reduction') || ''}
          possibility={_.get(data, 'reassessed_risk_possibility_level') || 0}
          severity={_.get(data, 'reassessed_risk_severity_level') || 0}
          warning_threshold={warning_threshold}
        />
      </Stack>
    </Box>
  );
};

const Risk = ({ riskPoint, possibility, severity, index }) => {
  return (
    <Box sx={{ width: '40%' }}>
      <Box sx={{ display: 'flex' }}>
        <span className='detail-ky-report__description '>
          危険ポイント{index}
        </span>
      </Box>
      <div className='detail-ky-report__title'>{riskPoint}</div>
      <Stack direction='row' alignItems='center'>
        <Box sx={{ width: '20%', textAlign: 'center' }} className='text-title'>
          <span className='detail-ky-report__title'>{`可能性 ${possibility}`}</span>
        </Box>
        <Box sx={{ width: '20%', textAlign: 'center' }} className='text-title'>
          <img src={MultiplicationIcon} alt='' />
        </Box>
        <Box
          sx={{ width: '20%', textAlign: 'center' }}
          className='text-description'
        >
          <span className='detail-ky-report__title'>{`重大性 ${severity ||
            0}`}</span>
        </Box>
        <Box sx={{ width: '20%', textAlign: 'center' }} className='text-title'>
          <img src={EqualIcon} alt='' />
        </Box>
        <Box sx={{ width: '20%', textAlign: 'center' }} className='text-title'>
          <span className='detail-ky-report__title'>{`評価値 ${possibility *
            severity}`}</span>
        </Box>
      </Stack>
    </Box>
  );
};

const ReassessedRisk = ({
  riskReduction,
  possibility,
  severity,
  warning_threshold,
}) => {
  const isWarning = possibility * severity >= warning_threshold;
  return (
    <Box sx={{ width: '40%' }}>
      <Box sx={{ display: 'flex' }}>
        <span className='detail-ky-report__description'>低減対策</span>
      </Box>
      <div className='detail-ky-report__title'>{riskReduction}</div>
      <Stack direction='row' alignItems='center'>
        <Box sx={{ width: '20%', textAlign: 'center' }} className='text-title'>
          <span className='detail-ky-report__title'>{`可能性 ${possibility}`}</span>
        </Box>
        <Box sx={{ width: '20%', textAlign: 'center' }} className='text-title'>
          <img src={MultiplicationIcon} alt='' />
        </Box>
        <Box
          sx={{ width: '20%', textAlign: 'center' }}
          className='text-description'
        >
          <span className='detail-ky-report__title'>{`重大性 ${severity}`}</span>
        </Box>
        <Box sx={{ width: '20%', textAlign: 'center' }} className='text-title'>
          <img src={EqualIcon} alt='' />
        </Box>
        <Box sx={{ width: '20%', textAlign: 'center' }} className='text-title'>
          <span
            className={`detail-ky-report__title ${
              isWarning ? 'warning-title' : ''
            }`}
          >{`評価値 ${possibility * severity}`}</span>
        </Box>
      </Stack>
    </Box>
  );
};

const ReportParticipants = ({ data, images }) => {
  return (
    <React.Fragment>
      <div className='detail-ky-report__title'>{`参加者：${_.size(
        data
      )}名`}</div>
      <Grid className='detail-ky-report__box-wrapper'>
        <Stack direction='row' spacing={2}>
          <Box sx={{ width: '536px', flexShrink: '0' }} >
            <Grid container spacing={1} sx={{ marginTop: '0px' }}>
              {_.map(images, (image, index) => (
                <Box key={`participant-image-${index}`}>
                  <img
                    src={image.url}
                    alt=''
                    className='detail-ky-report__participant-image'
                  />
                </Box>
              ))}
            </Grid>
          </Box>
          <Box sx={{ width: 'calc(100% - 536px)' }}>
            <Stack spacing={0.5}>
              {_.map(data, (kyReportParticipant, index) => (
                <ParticipantRow
                  key={index}
                  kyReportParticipant={kyReportParticipant}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </Grid>
    </React.Fragment>
  );
};

const ParticipantRow = ({ kyReportParticipant }) => {
  const {
    entrance_worker_id: entranceWorkerId,
    company_name: companyName,
    worker_name: workerName,
  } = kyReportParticipant;
  return (
    <Stack direction='row' spacing={1} alignItems='center'>
      <Box
        className={
          _.isNil(entranceWorkerId)
            ? 'detail-ky-report__blank-span'
            : 'detail-ky-report__chip-label'
        }
      >
        {_.isNil(entranceWorkerId) ? '' : '入退'}
      </Box>
      <Box className='detail-ky-report__highlight'>
        {workerName}
        <span className='detail-ky-report__description--title break-word'>
          &nbsp;{companyName}
        </span>
      </Box>
    </Stack>
  );
};

const ReportConfirm = ({ data }) => {
  const [leftItems, rightItems] = splitConfirmItems(data);

  return (
    <React.Fragment>
      <div className='detail-ky-report__title'>確認項目</div>
      <Box className='detail-ky-report__box-wrapper'>
        <Stack direction='row' spacing={2.5}>
          <Box sx={{ width: '50%' }}>
            <Stack spacing={1}>
              {_.map(leftItems, (confirmItem, index) => (
                <ConfirmationItem
                  key={`left-${index}`}
                  label={confirmItem.label}
                  value={confirmItem.value}
                />
              ))}
            </Stack>
          </Box>
          <Box sx={{ width: '50%' }}>
            <Stack spacing={1}>
              {_.map(rightItems, (confirmItem, index) => (
                <ConfirmationItem
                  key={`right-${index}`}
                  label={confirmItem.label}
                  value={confirmItem.value}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </Box>
    </React.Fragment>
  );
};

const ContactNote = ({ data }) => {
  return (
    <React.Fragment>
      <div className='detail-ky-report__title'>連絡事項</div>
      <div className='detail-ky-report__description--title break-word detail-ky-report__box-wrapper'>
        {_.get(data, 'contact_note') || ''}
      </div>
    </React.Fragment>
  );
};

const ConfirmationItem = ({ label, value }) => (
  <Stack spacing={1} direction='row'>
    <Box className='detail-ky-report__description--title break-word'>
      {label}
      <span className='detail-ky-report__highlight'>&nbsp;{value}</span>
    </Box>
  </Stack>
);

const FocusAction = ({ focusAction }) => {
  return (
    <React.Fragment>
      <div className='detail-ky-report__title'>本日の重点項目/行動目標等</div>
      <Box className='detail-ky-report__box-wrapper'>
        <div className='detail-ky-report__description--title break-word'>
          {focusAction}
        </div>
      </Box>
    </React.Fragment>
  );
};

const CommentArea = ({ comment, onCommentChange }) => {
  return (
    <React.Fragment>
      <div className='detail-ky-report__title'>元請コメント</div>
      <Box className='detail-ky-report__box-wrapper'>
        <textarea
          className='detail-ky-report__description--title detail-ky-report__comment-area'
          value={comment}
          onChange={onCommentChange}
          maxLength={255}
        />
      </Box>
    </React.Fragment>
  );
};

const RejectConfirm = ({
  openConfirmDialog,
  openRejectDialog,
  kyReportStatus,
}) => {
  const isApproved = kyReportStatus === KY_STATUS_CONFIG.APPROVED;
  const isRejected = kyReportStatus === KY_STATUS_CONFIG.REJECT;
  return (
    <Box>
      <Stack
        direction='row'
        spacing={3}
        alignItems='center'
        justifyContent='center'
      >
        <button
          className={`detail-ky-report__button-update-status ${
            isRejected ? 'btn--disabled' : 'btn--negative--filled'
          }`}
          onClick={openRejectDialog}
          disabled={isRejected}
        >
          差戻し
        </button>
        <button
          className={`detail-ky-report__button-update-status ${
            isApproved ? 'btn--disabled' : 'btn--effective--filled'
          }`}
          onClick={openConfirmDialog}
          disabled={isApproved}
        >
          {messageUtility.getMessage('E-000-005-01')}
        </button>
        <hr className='boundary-with-item-list' />
      </Stack>
    </Box>
  );
};

const DetailKYFooter = ({ hasNavigate, navigate }) => {
  return (
    <Stack
      maxHeight='20px'
      direction='row'
      justifyContent='space-between'
      alignItems='center'
    >
      <Stack direction='row' spacing={1}>
        <Box
          sx={{
            opacity: hasNavigate ? undefined : 0.2,
            cursor: hasNavigate ? 'pointer' : 'default',
          }}
        >
          <img
            className='icon--2x'
            src={PreviousKYIcon}
            alt=''
            onClick={() => hasNavigate && navigate(false)}
          />
        </Box>
        <Box>
          <span
            className={`detail-ky-report__description--text-link ${
              hasNavigate ? 'clickable-text' : 'disabled-text'
            }`}
            onClick={() => hasNavigate && navigate(false)}
          >
            前の現地KYを確認
          </span>
        </Box>
      </Stack>
      <Stack direction='row' spacing={0.5}>
        <Box>
          <span
            className={`detail-ky-report__description--text-link ${
              hasNavigate ? 'clickable-text' : 'disabled-text'
            }`}
            onClick={() => hasNavigate && navigate(true)}
          >
            次の現地KYを確認
          </span>
        </Box>
        <Box
          sx={{
            opacity: hasNavigate ? undefined : 0.2,
            cursor: hasNavigate ? 'pointer' : 'default',
          }}
        >
          <img
            className='icon--2x'
            src={NextKYIcon}
            alt=''
            onClick={() => hasNavigate && navigate(true)}
          />
        </Box>
      </Stack>
    </Stack>
  );
};

/**
 * @description 入力配列から確認項目を抽出し、タイトルと内容のグループに分割する.
 * @param {Object} rawItems - 確認項目の設定.
 */
const splitConfirmItems = rawItems => {
  const items = _.chain(_.range(1, 11))
    .map(i => {
      const title = _.get(rawItems, `confirm_item_${i}_title`);
      const value = _.get(rawItems, `confirm_item_${i}_value`);

      return title ? { label: title, value } : null;
    })
    .compact()
    .value();

  return [_.slice(items, 0, 5), _.slice(items, 5, 10)];
};

const isShowConfigItem = settingInfo =>
  _.some(settingInfo, item => item !== null);



// WEBPACK FOOTER //
// ./src/components/DetailKYReport.jsx